<template>
    <MainContainer>
        <template #aboveNavSidebar>
            <p class="small mb-1">Gewähltes Schuljahr</p>
            <SchoolYearSwitcher class="mb-3" />
        </template>
        <div class="schulen-list">
            <h2>
                Liste Schulstammdaten
            </h2>
            <Table
                bordered
                striped
                showFilter
                :responsive="true"
                :fields="fields"
                :items="items"
                :isBusy="busy"
                busyText="Lädt Schul-Informationen..."
                emptyText="Keine Schul-Informationen gefunden"
                sortBy="mitglied_name"
                tableClass="schulen-list-table"
            >
                <template v-slot:thead-top="{ data: data }">
                    <b-tr class="bg-light text-dark">
                        <b-th class="text-center">
                            <b-checkbox
                                v-model="multiSelect"
                                :disabled="false"
                                @change="selectAll"
                            />
                        </b-th>
                        <b-th colspan="6" class="text-left">
                            <span class="small mx-1"
                                >Auf die Gesamtauswahl bezogen:</span
                            >
                            <b-button
                                class="mx-1"
                                variant="warning"
                                :disabled="extending.busy"
                                :size="size"
                                @click="extendSelection"
                                ><font-awesome-icon
                                    icon="exclamation-triangle"
                                    class="text-dark mr-1"
                                />Verträge verlängern</b-button
                            >
                        </b-th>
                        <b-th colspan="13" class="text-left">
                            <span class="small mx-1"
                                >Allgemeine Funktionen:</span
                            >
                            <b-button
                                class="mx-1"
                                variant="secondary"
                                :disabled="printing.busy"
                                :size="size"
                                @click="printListPDF"
                                ><font-awesome-icon
                                    icon="print"
                                    class="text-light mr-1"
                                />Drucken</b-button
                            >
                        </b-th>
                    </b-tr>
                </template>

                <template v-slot:selected="{ row: row }">
                    <b-checkbox v-model="row.item.selected" :disabled="false" />
                </template>

                <template v-slot:name="{ row: row }">
                    <b-link
                        :to="{
                            path: '/mitglieder/' + row.item.id
                        }"
                        >{{ row.value }}</b-link
                    >
                </template>

                <template v-slot:additiv="{ row: row }">
                    {{ formatDecimals(row.value) }}
                </template>

                <template v-slot:gtk="{ row: row }">
                    {{ formatDecimals(row.value) }}
                </template>

                <template v-slot:ogs="{ row: row }">
                    {{ formatDecimals(row.value) }}
                </template>

                <template v-slot:fv="{ row: row }">
                    {{ currency(row.value) }}
                </template>

                <template v-slot:anteil="{ row: row }">
                    {{ currency(row.value) }}
                </template>

                <template v-slot:sl="{ row: row }">
                    {{ currency(row.value) }}
                </template>

                <template v-slot:fehl_sl="{ row: row }">
                    {{ currency(row.value) }}
                </template>

                <template v-slot:overhead="{ row: row }">
                    {{ currency(row.value) }}
                </template>

                <template v-slot:fehl="{ row: row }">
                    {{ currency(row.value) }}
                </template>

                <template v-slot:actions="{ row: row }">
                    <b-dropdown text="Aktionen" :size="size" dropleft>
                        <b-dropdown-item
                            :to="{
                                path: '/mitglieder/' + row.id
                            }"
                        >
                            <font-awesome-icon
                                icon="exchange-alt"
                                class="text-primary mr-1"
                            />Zum Mitglied
                        </b-dropdown-item>
                        <b-dropdown-item
                            v-if="checkRight('write')"
                            :disabled="extending.busy"
                            @click="extendMultipleItems(row.related_ids)"
                        >
                            <font-awesome-icon
                                icon="exclamation-triangle"
                                class="text-warning mr-1"
                            />Vetrag verlängern
                        </b-dropdown-item>
                    </b-dropdown>
                </template>

                <template v-slot:custom-foot="{ data: data }">
                    <b-tr class="bg-light text-dark">
                        <b-th class="text-center">
                            <b-checkbox
                                v-model="multiSelect"
                                :disabled="false"
                                @change="selectAll"
                            />
                        </b-th>
                        <b-th></b-th>
                        <b-th></b-th>
                        <b-th class="text-right">
                            {{ formatDecimals(sums.additiv) }}
                        </b-th>
                        <b-th class="text-right">
                            {{ formatDecimals(sums.gtk) }}
                        </b-th>
                        <b-th class="text-right">
                            {{ formatDecimals(sums.ogs) }}
                        </b-th>
                        <b-th class="text-right">
                            {{ currency(sums.fv) }}
                        </b-th>
                        <b-th class="text-right">
                            {{ currency(sums.anteil) }}
                        </b-th>
                        <b-th class="text-right">
                            {{ currency(sums.sl) }}
                        </b-th>
                        <b-th class="text-right">
                            {{ currency(sums.fehl_sl) }}
                        </b-th>
                        <b-th class="text-right">
                            {{ currency(sums.overhead) }}
                        </b-th>
                        <b-th class="text-right">
                            {{ currency(sums.fehl) }}
                        </b-th>
                        <b-th class="text-right">
                            {{ sums.sp_kl }}
                        </b-th>
                        <b-th class="text-right">
                            {{ sums.schueler }}
                        </b-th>
                        <b-th class=""> </b-th>
                    </b-tr>
                </template>
            </Table>
        </div>
    </MainContainer>
</template>

<script>
import CommonView from "@/mixins/CommonView/CommonView";
import Size from "@/mixins/Size/Size";
import Currency from "@/mixins/Currency/Currency";
import SingPause from "@/mixins/SingPause/SingPause";
import SingPauseOGS from "@/mixins/SingPause/SingPauseOGS";
import { ApiFactory } from "@/api/ApiFactory.js";
import { BlobUtil } from "@/utils/BlobUtil.js";
import MainContainer from "@/components/MainContainer/MainContainer";
import Table from "@/components/Table/Table";
import SchoolYearSwitcher from "@/components/SchoolYearSwitcher/SchoolYearSwitcher";

import "./SchulenList.scss";

const SingPauseApi = ApiFactory.get("singpause");

export default {
    name: "SchulenList",
    mixins: [CommonView, Size, Currency, SingPause, SingPauseOGS],
    components: {
        MainContainer,
        Table,
        SchoolYearSwitcher
    },
    data() {
        return {
            fields: [
                {
                    key: "selected",
                    label: "",
                    class: "text-center",
                    sortable: false
                },
                {
                    key: "id",
                    label: "ID",
                    sortable: true
                },
                {
                    key: "name",
                    label: "Name",
                    sortable: true
                },
                {
                    key: "additiv",
                    label: "Additiv",
                    class: "text-right",
                    sortable: true
                },
                {
                    key: "gtk",
                    label: "GTK",
                    class: "text-right",
                    sortable: true
                },
                {
                    key: "ogs",
                    label: "OGS",
                    class: "text-right",
                    sortable: true
                },
                {
                    key: "fv",
                    label: "FV",
                    class: "text-right",
                    sortable: true
                },
                {
                    key: "anteil",
                    label: "Anteil",
                    class: "text-right",
                    sortable: false
                },
                {
                    key: "sl",
                    label: "Honorare SL",
                    class: "text-right",
                    sortable: false
                },
                {
                    key: "fehl_sl",
                    label: "Fehlbe.-SL",
                    class: "text-right",
                    sortable: false
                },
                {
                    key: "overhead",
                    label: "Overhead",
                    class: "text-right",
                    sortable: false
                },
                {
                    key: "fehl",
                    label: "Fehlbe.-SU",
                    class: "text-right",
                    sortable: false
                },
                {
                    key: "sp_kl",
                    label: "Anz. Klassen",
                    class: "text-right",
                    sortable: true
                },
                {
                    key: "schueler",
                    label: "Schüler",
                    class: "text-right",
                    sortable: true
                },
                {
                    key: "actions",
                    label: "Funktionen",
                    class: "text-center"
                }
            ],
            api: SingPauseApi,
            multiSelect: false,
            sums: {}
        };
    },
    watch: {
        selectedSchoolYear: {
            handler() {
                this.fetch();
            }
        }
    },
    methods: {
        async fetch(busyState = true) {
            this.busy = busyState;
            const response = await this.api.getSchoolsInYear(
                this.selectedSchoolYear,
                this.showInactive
            );
            if (response.ok) {
                var items = response.json.list;
                this.items = items;
                this.registerRights(response);

                this.sums = response.json.sums;
            }
            this.busy = false;
        },

        selectAll(val) {
            this.items = this.items.map(item => {
                item.selected = val;
                return item;
            });
        },
        extendSelection() {
            const stackedIds = this.items
                .filter(item => item.selected)
                .map(item => item.related_ids);
            console.log(stackedIds);
            var ids = [];
            for (const arr of stackedIds) {
                for (const val of arr) {
                    ids.push(val);
                }
            }
            this.extendMultipleItems(ids);
        },
        async printListPDF() {
            this.printing.busy = true;
            const response = await SingPauseApi.getSchoolsInYearPDF(
                this.selectedSchoolYear
            );
            if (response.ok) {
                BlobUtil.downloadBlob(
                    response.blob,
                    "Liste Schulstammdaten.pdf"
                );
            }
            this.printing.busy = false;
        },
        formatDecimals(number, returnEmpty = false) {
            if (number) {
                return parseFloat(number).toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2
                });
            } else {
                if (returnEmpty) {
                    return "";
                } else {
                    return parseFloat(0).toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2
                    });
                }
            }
        }
    }
};
</script>
